class TimelineScroller extends HTMLElement {
  connectedCallback() {
    this.imageContainer = this.querySelector('[data-image-container]');
    this.overlay = this.imageContainer?.querySelector('.inspiration-timeline__overlay');
    this.steps = Array.from(this.querySelectorAll('[data-step]'));

    if (!this.imageContainer || this.steps.length === 0) return;

    this.layers = new Map();
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.style.setProperty('--timeline-step-count', this.steps.length);
    this.style.setProperty('--timeline-scroll-segments', this.steps.length + 1);
    this.scrollContainer = this._getScrollContainer();

    this._buildLayers();
    this._bindClicks();
    this._bindScroll();

    this._setActive(this.steps[0]?.dataset.stepId);
  }

  _buildLayers() {
    this.steps.forEach((step) => {
      const stepId = step.dataset.stepId;
      if (!stepId) return;

      /* Avoid duplicated layers after Shopify theme-editor reconnects. */
      const existingLayer = this.imageContainer.querySelector(
        `.inspiration-timeline__image-layer[data-step-id="${CSS.escape(stepId)}"]`
      );

      if (existingLayer) {
        this.layers.set(stepId, existingLayer);
        return;
      }

      const template = this.querySelector(
        `template[data-step-image][data-step-id="${CSS.escape(stepId)}"]`
      );
      if (!template) return;

      const layer = document.createElement('div');
      layer.className = 'inspiration-timeline__image-layer';
      layer.dataset.stepId = stepId;
      layer.appendChild(template.content.cloneNode(true));

      if (this.overlay) {
        this.imageContainer.insertBefore(layer, this.overlay);
      } else {
        this.imageContainer.appendChild(layer);
      }

      this.layers.set(stepId, layer);
    });
  }

  _bindClicks() {
    this.steps.forEach((step, index) => {
      const activate = () => {
        this._setActive(step.dataset.stepId);

        if (this.mediaQuery.matches) {
          step.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          return;
        }

        const isWindow = this.scrollContainer === document.scrollingElement;
        const scrollTarget = isWindow ? window : this.scrollContainer;
        const currentScroll = isWindow ? window.scrollY : this.scrollContainer.scrollTop;
        const containerTop = isWindow ? 0 : this.scrollContainer.getBoundingClientRect().top;
        const timelineTop = currentScroll + this.getBoundingClientRect().top - containerTop;

        scrollTarget.scrollTo({
          top: timelineTop + index * window.innerHeight,
          behavior: 'smooth',
        });
      };

      step.addEventListener('click', activate);
      step.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      });
    });
  }

  _getScrollContainer() {
    let parent = this.parentElement;

    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const canScroll = /(auto|scroll|overlay)/.test(style.overflowY);

      if (canScroll && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return document.scrollingElement;
  }

  _bindScroll() {
    this._onScroll = () => {
      if (this.mediaQuery.matches || this._scrollFrame) return;

      this._scrollFrame = requestAnimationFrame(() => {
        this._scrollFrame = null;

        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight || 1;
        const lastIndex = this.steps.length - 1;

        /* One viewport of page travel advances exactly one slide. */
        const index = Math.max(
          0,
          Math.min(lastIndex, Math.round(-rect.top / viewportHeight))
        );

        this._setActive(this.steps[index]?.dataset.stepId);
      });
    };

    this.scrollTarget = this.scrollContainer === document.scrollingElement
      ? window
      : this.scrollContainer;

    this.scrollTarget.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    this.mediaQuery.addEventListener?.('change', this._onScroll);
    this._onScroll();
  }

  _setActive(stepId) {
    if (!stepId || !this.layers.has(stepId)) return;

    this.steps.forEach((step) => {
      step.toggleAttribute('data-active', step.dataset.stepId === stepId);
    });

    this.layers.forEach((layer, id) => {
      layer.toggleAttribute('data-active', id === stepId);
    });
  }

  disconnectedCallback() {
    this.scrollTarget?.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
    this.mediaQuery?.removeEventListener?.('change', this._onScroll);

    if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
  }
}

if (!customElements.get('timeline-scroller')) {
  customElements.define('timeline-scroller', TimelineScroller);
}
